import { useCallback } from 'react';

import { useSsoPageRoleMappingDeleteMutation } from '../../../generated';
import type { SsoRoleMappingDeleteResult } from '../types';

export interface DeleteSsoRoleMappingInput {
  connectionId: string;
  federatedRole: string;
}

export interface UseSsoRoleMappingDeleteResult {
  deleteRoleMapping: (
    input: DeleteSsoRoleMappingInput
  ) => Promise<SsoRoleMappingDeleteResult>;
  isDeleting: boolean;
}

export const useSsoRoleMappingDelete = (): UseSsoRoleMappingDeleteResult => {
  const [mutate, { loading }] = useSsoPageRoleMappingDeleteMutation({
    refetchQueries: ['SsoPageRoleMappings'],
    awaitRefetchQueries: true,
  });

  const deleteRoleMapping = useCallback(
    async ({
      connectionId,
      federatedRole,
    }: DeleteSsoRoleMappingInput): Promise<SsoRoleMappingDeleteResult> => {
      const result = await mutate({
        variables: { input: { connectionId, federatedRole } },
      });

      const [transportError] = result.errors ?? [];
      const response = result.data?.ssoRoleMappingDelete;

      if (!response) {
        return {
          kind: 'request',
          message:
            transportError?.message ?? 'The role mapping could not be removed.',
        };
      }

      switch (response.__typename) {
        case 'SsoRoleMappingDeleteSuccess':
          return { kind: 'success' };
        case 'NotFoundError':
          return {
            kind: 'notFound',
            message: response.message ?? 'That role mapping is already gone.',
          };
        case 'InputValidationFailure':
          return {
            kind: 'validation',
            message:
              response.message ?? 'The role mapping could not be removed.',
            invalidFields:
              response.invalidFields?.map((field) => ({
                fieldName: field.fieldName,
                reason: field.reason,
              })) ?? [],
          };
        case 'AuthorizationFailure':
          return {
            kind: 'authorization',
            message:
              response.message ?? 'Not authorized to change role mappings.',
          };
        default:
          return {
            kind: 'request',
            message: 'The role mapping could not be removed.',
          };
      }
    },
    [mutate]
  );

  return { deleteRoleMapping, isDeleting: loading };
};
